import { useCallback, useMemo } from "react";
import {
  EngagementSurveyReportGroupBy,
  EngagementSurveyRollupType,
  GroupedAnswersNode,
} from "types/graphql-schema";

import EngagementSurveyGroupBySelector from "@apps/reporting/components/engagement-survey-report/engagement-survey-group-by-selector";
import EngagementSurveyReportFilterContainer from "@apps/reporting/components/engagement-survey-report/engagement-survey-report-filter-container";
import {
  EngagementSurveyReportFilterItem,
  EngagementSurveyReportViewOptions,
  EngagmentSurveyGroupBySelectorItem,
} from "@apps/reporting/components/engagement-survey-report/types";
import ViewOptionsSidebar from "@apps/reporting/components/sidebar/view-options-sidebar";

const EngagementSurveyDeliveryFilterSidebar = ({
  viewOptions,
  onUpdateViewOptions,
  groupedAnswers,
  onClose,
  isLoading,
}: {
  viewOptions: EngagementSurveyReportViewOptions;
  onUpdateViewOptions: (
    opts: Partial<EngagementSurveyReportViewOptions>,
  ) => void;
  groupedAnswers: ReadonlyArray<GroupedAnswersNode>;
  onClose: () => void;
  isLoading: boolean;
}) => {
  const filters = viewOptions.filters.list;
  const groupBy = viewOptions.groupBy;
  const hiddenGroupKeys = viewOptions.hiddenColumns;

  const selectableGroupNames: EngagmentSurveyGroupBySelectorItem[] =
    useMemo(() => {
      if (groupedAnswers == null || groupedAnswers == undefined || !groupBy) {
        return [];
      }

      // Filter out null values and count responders per group label, excluding COLLAPSED rollup type (Other groups)
      const filteredAnswers = groupedAnswers.filter(
        (answer): answer is GroupedAnswersNode => answer !== null,
      );
      const groupCounts = new Map<string, { key: string; count: number }>();
      for (const groupedAnswer of filteredAnswers) {
        // Skip groups with COLLAPSED rollup type (these are "Other" groups)
        if (groupedAnswer.rollupType === EngagementSurveyRollupType.Collapsed) {
          continue;
        }

        const key = groupedAnswer.groupKey;
        const existing = groupCounts.get(key);

        if (existing) {
          existing.count = Math.max(
            existing.count,
            groupedAnswer.numberResponders,
          );
        } else {
          groupCounts.set(key, {
            key,
            count: groupedAnswer.numberResponders,
          });
        }
      }

      // Convert to array and sort by count (highest first)
      return Array.from(groupCounts.entries())
        .sort(([, a], [, b]) => b.count - a.count)
        .map(([key]) => {
          // Get label from one of the grouped answers with this key
          const label =
            groupedAnswers?.find((ga) => ga?.groupKey === key)?.groupLabel ||
            key;

          return { key, label };
        });
    }, [groupedAnswers, groupBy]);

  const handleUpdateFilters = useCallback(
    (newFilters: EngagementSurveyReportFilterItem[]) => {
      onUpdateViewOptions({
        filters: {
          ...viewOptions.filters,
          list: newFilters,
        },
      });
    },
    [onUpdateViewOptions, viewOptions.filters],
  );

  const handleChangeGroupBy = useCallback(
    (newGroupBy: EngagementSurveyReportGroupBy | null) => {
      onUpdateViewOptions({
        groupBy: newGroupBy,
        hiddenColumns: [], // Reset hidden groups when changing group type
      });
    },
    [onUpdateViewOptions],
  );

  const handleChangeHiddenItems = useCallback(
    (newHiddenColumns: string[]) => {
      onUpdateViewOptions({ hiddenColumns: newHiddenColumns });
    },
    [onUpdateViewOptions],
  );

  return (
    <ViewOptionsSidebar onClose={onClose}>
      <div className="border-t p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold">Filters</div>
        </div>
        <div>
          <EngagementSurveyReportFilterContainer
            filters={filters}
            onUpdateFilters={handleUpdateFilters}
          />
        </div>
      </div>

      <EngagementSurveyGroupBySelector
        groupBy={groupBy}
        onChangeGroupBy={handleChangeGroupBy}
        title="Group Comments By"
        includeNoneOption={true}
        selectableItems={selectableGroupNames}
        hiddenItemKeys={hiddenGroupKeys}
        onChangeHiddenItems={handleChangeHiddenItems}
        infoMessage="Unselected groups will be grouped under 'Other'."
        isLoading={isLoading}
      />
    </ViewOptionsSidebar>
  );
};

export default EngagementSurveyDeliveryFilterSidebar;
